import { useTranslation } from "react-i18next";
import { useActiveSpecialModes, SPECIAL_GAME_MODES } from "@ugg/shared/utils/special-game-modes";
import { useGlobal } from "reactn";
import { useParams, useLocation } from "react-router-dom";
import { getQueueTypeName } from "@ugg/shared/utils/queue-type-helpers";
import { hasClassicCounterpart } from "@ugg/shared/utils/multi-build-helpers";
import { useUGGPatches } from "@ugg/shared/api/requests/patches";
import { PageNav, NavTab } from "@ugg/shared/components/common/PageNav";
import { championProfileUrlPages } from "@ugg/shared/pages/champion-pages";
import {
  getChampionBuildUrl,
  getChampionCountersUrl,
  getChampionMatchupsUrl,
  getChampionItemsUrl,
  getChampionItemPathsUrl,
  getChampionRunesTableUrl,
  getChampionRuneSetsUrl,
  getChampionSpellsUrl,
  getChampionDuosUrl,
  getChampionLeaderboard,
  AppRoutes,
  getSpecialGameModeChampionBuildUrl,
  getClassicChampionBuildUrl,
} from "@ugg/shared/routes/app-routes";

export function ChampionProfilePageNav({ style = {}, className }: { style?: React.CSSProperties; className?: string }) {
  const { t } = useTranslation();
  const [language] = useGlobal("language");
  const { champion } = useParams<{ champion: string }>();
  const location = useLocation();

  // Show a "Classic" tab (with NEW tag) only for champions that have a League
  // Classic counterpart. Gated by a hardcoded alias roster so no riot-assets
  // lookup or manifest fetch is needed — the URL alias alone decides it, which
  // keeps the tab SSR-stable (no post-hydration flicker); see hasClassicCounterpart.
  // Both shells route CHAMPION_PROFILE_BUILD_CLASSIC now (web MainContent.js
  // and monoapp MainContent.tsx), so this is roster-gated only.
  //
  // The page's effective patch is passed so roster additions can be pre-deployed
  // and stay inert until their activation patch ships — see
  // CLASSIC_COUNTERPART_ALIASES_BY_PATCH.
  //
  // This adds no request and no new SSR requirement. The champion page already
  // resolves a patch with `ssr: true` — useValidateQueryParams(validParams, true)
  // calls useUGGPatches({ ssr: true }) internally, and both ChampionProfile and
  // ChampionProfileProvider go through it (it's the same patch that reaches the
  // SEO title, so it has to be valid at server render). useJSONFetcher is
  // URL-keyed, so this shares that cache slot. `ssr: true` is required, not
  // decorative: without it a pre-deployed champion's tab would be missing from
  // the server render and pop in after hydration — the exact flicker the
  // hardcoded roster exists to prevent.
  //
  // Resolved here rather than through useValidateQueryParams because that hook
  // also pulls useChampionMini, which is the async id lookup the alias roster was
  // written to avoid depending on. It plays no part in resolving `patch`, so
  // taking the patch list directly keeps the nav's dependencies unchanged.
  //
  // `?patch=` is only honored if it's a real patch. Anything else — a fabricated
  // future value, a typo, a patch outside this page's window — falls back to
  // latest, so a hand-edited URL can't force an activation group on early.
  //
  // Filter-aware on purpose: viewing 16_15 data must not show a tab for a
  // champion whose counterpart only exists from 16_16 on, because the Classic
  // page it links to has no data for that patch.
  const { data: uggPatches } = useUGGPatches({ ssr: true });
  const queryPatch = new URLSearchParams(location.search).get("patch") || undefined;
  const effectivePatch = (uggPatches || []).some((p) => p === queryPatch) ? queryPatch : uggPatches?.[0];
  const showClassicTab = hasClassicCounterpart(champion, effectivePatch);
  const classicTab: NavTab[] = showClassicTab
    ? [
        {
          to: getClassicChampionBuildUrl(champion),
          label: t("Classic"),
          reset: true,
          new: true,
          activePaths: [AppRoutes.CHAMPION_PROFILE_BUILD_CLASSIC],
        },
      ]
    : [];

  const activeModes = useActiveSpecialModes({ isChampBuildPage: true, ssr: true });
  const specialModeTabs: NavTab[] = [];
  activeModes.forEach((mode) => {
    const { urlName, buildPageName, newTag } = SPECIAL_GAME_MODES[mode];
    return specialModeTabs.push({
      to: getSpecialGameModeChampionBuildUrl(champion, urlName),
      label: getQueueTypeName(mode),
      reset: true,
      new: newTag,
      activePaths: [championProfileUrlPages[buildPageName]],
    });
  });

  const tabs: NavTab[] = [
    {
      to: getChampionBuildUrl(champion, { language }),
      label: t("Build"),
      activePaths: [AppRoutes.CHAMPION_PROFILE_BUILD],
    },
    ...classicTab,
    ...specialModeTabs,
    { to: getChampionCountersUrl(champion), label: t("Counters") },
    {
      to: getChampionLeaderboard(champion),
      label: t("Leaderboards"),
    },
    {
      id: "probuilds",
      customComponent: (
        <a
          className="pbs-tab flex items-center"
          target="_blank"
          href={`https://probuildstats.com/champion/${champion}`}
          rel="noreferrer"
          draggable={false}
        >
          <div className="pbs-logo inline-flex relative top-[1px] w-[13px] mr-[10px]">
            <img
              className="w-full"
              src="https://static.bigbrain.gg/assets/probuildstats/logos/pbs-logo.svg"
              alt="ProbuildStats"
            />
          </div>
          {t("Pro Builds")}
        </a>
      ),
    },
    {
      label: t("More Stats"),
      dropdown: [
        { to: getChampionMatchupsUrl(champion), label: t("Matchups") },
        { to: getChampionItemsUrl(champion), label: t("Items") },
        { to: getChampionItemPathsUrl(champion), label: t("Build Paths") },
        { to: getChampionRunesTableUrl(champion), label: t("Runes Table") },
        { to: getChampionRuneSetsUrl(champion), label: t("Rune Sets") },
        { to: getChampionSpellsUrl(champion), label: t("Spells & Abilities") },
        { to: getChampionDuosUrl(champion), label: t("Duos") },
      ],
    },
  ];

  return <PageNav className={className} tabs={tabs} />;
}
